/**
 * Pricing failure reporting — the input side of the repair loop.
 *
 * Two things write here. The app itself, whenever a card fails to price
 * (trigger A), and the user, through the report button on card detail
 * (trigger B). A nightly crawler writes the same table from the server side.
 *
 * Every write is fire-and-forget. Reporting a pricing failure must never turn
 * into a second failure on screen, so nothing in this module throws and nothing
 * in it is awaited by the pricing path.
 *
 * Clustering happens in Postgres on insert. The client does not compute the
 * fingerprint and should not try to — one hash implementation, in one place,
 * or the clusters drift apart.
 */

import { supabase } from '../lib/supabase';

/**
 * Where in the pricing path it broke. The whole point of the loop is that a
 * failure arrives already knowing which gate rejected it, so the diagnosis does
 * not have to re-run the investigation blind.
 */
export const FAILURE_STAGES = {
  NO_API_KEY:      'no_api_key',       // key missing from the build
  SEARCH_ERROR:    'search_error',     // vendor returned a non-200
  SEARCH_EMPTY:    'search_empty',     // vendor returned zero candidates
  NO_NUMBER_MATCH: 'no_number_match',  // candidates came back, none had the number
  NOT_CATALOGUED:  'not_catalogued',   // the vendor has the card but not this printing
  NO_NAME_MATCH:   'no_name_match',    // candidates came back, none of them this card
  NO_SET_MATCH:    'no_set_match',     // number matched, set name did not
  NO_FINISH_MATCH: 'no_finish_match',  // set matched, finish did not
  NO_STAMP_MATCH:  'no_stamp_match',   // the vendor has the card, not this stamping
  NO_PRICE_DATA:   'no_price_data',    // matched a card, pricing fetch failed
  EMPTY_TIERS:     'empty_tiers',      // matched and priced, but no rows for this market
  NO_LISTINGS:     'no_listings',      // vendor gave tier averages and zero individual sales
  AMBIGUOUS_MATCH: 'ambiguous_match',  // two candidates tie; showing either would be a guess
};

/** Reasons offered to the user on the report sheet. Structured, because free text does not cluster. */
export const REPORT_REASONS = [
  { code: 'no_price',      label: 'No price is showing at all' },
  { code: 'missing_source', label: 'One source is missing (TCG, eBay)' },
  { code: 'wrong_finish',  label: 'Wrong finish (holo, reverse, normal)' },
  { code: 'wrong_number',  label: 'Wrong card number' },
  { code: 'wrong_card',    label: 'This is a different card' },
  { code: 'price_too_high', label: 'Price looks too high' },
  { code: 'price_too_low', label: 'Price looks too low' },
  { code: 'other',         label: 'Something else' },
];

// Reported already in this session. The database deduplicates per card per day,
// but there is no reason to send the request fifty times to find that out.
const sent = new Set();

function sessionKey(source, card, stage, reasonCode) {
  return [
    source,
    stage,
    reasonCode || '',
    card?.name || '',
    card?.set_name || '',
    card?.set_number || '',
    card?.variant || '',
  ].join('|').toLowerCase();
}

// A collection row's id is a uuid. A catalogue card's is not: it is the
// catalogue's own id, "xy12-109". card_row_id is a uuid column, so passing the
// catalogue id was rejected outright and every report on a card the person did
// not own failed with "that did not send" - which is exactly the card most
// likely to have something wrong with its price.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function snapshot(card) {
  const id = card?.id || null;
  const owned = id && UUID.test(id);
  return {
    card_row_id:    owned ? id : null,
    card_name:      card?.name || 'Unknown',
    set_name:       card?.set_name || null,
    set_number:     card?.set_number || null,
    variant:        card?.variant || null,
    grade:          card?.grade || null,
    // The catalogue id still gets recorded, just in the column that can hold
    // it, so a report on a card nobody owns is still traceable to a card.
    tcgdex_card_id: card?.card_id || (owned ? null : id),
  };
}

async function write(row) {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('pricing_reports')
      .insert({ ...row, user_id: auth?.user?.id || null });

    // A duplicate is the dedupe index doing its job, not a failure.
    if (error && error.code !== '23505') {
      console.warn('[PricingReport] write failed:', error.message);
    }
  } catch (err) {
    console.warn('[PricingReport] write threw:', err?.message);
  }
}

const money = (v) => (typeof v === 'number' && isFinite(v) ? Math.round(v * 100) / 100 : null);

/** A ladder group, reduced to the numbers a diagnosis actually reads. */
function trimLadder(group) {
  if (!group || typeof group !== 'object') return null;
  const out = {};
  for (const [key, t] of Object.entries(group)) {
    out[key] = {
      median30d: money(t?.median30d),
      low:       money(t?.low),
      high:      money(t?.high),
      saleCount: t?.saleCount ?? t?.count ?? 0,
    };
  }
  return Object.keys(out).length ? out : null;
}

/**
 * What was on screen when the user hit report.
 *
 * A complaint without the number it was about cannot be diagnosed later. The
 * ladder refreshes, and "the price looks too high" stops meaning anything once
 * the number it referred to is gone. So the report carries the ladder as it was
 * rendered, the vendor columns beside it, and the identity of the cached row it
 * all came from — which is usually the row that has to be fixed.
 *
 * Everything is trimmed on the way in. Evidence runs to hundreds of sales and
 * the point is a diagnosable snapshot, not a second copy of the cache.
 */
export function buildReportContext({ tiers, sources, row, selectedTier, card, failed, isTcgCard } = {}) {
  const asks = sources?.justTCG?.conditions || null;
  const evidence = Array.isArray(row?.evidence) ? row.evidence : [];
  const newest = [...evidence].sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));

  // Most reports arrive because nothing was on screen, so every field below is
  // null on the reports that matter most. This says which kind of nothing it
  // was, which is the part a pile of nulls cannot express.
  //
  //   not_priceable  sealed or a TCG entry, never had a price and never will
  //   no_row         nothing cached under this identity at all — a lookup miss
  //   row_empty      the row exists and holds no sales, so the fetch failed
  //   partial        sold prices are there, the vendor ask column is not
  //   priced         a full ladder was on screen and the user still says it is wrong
  const dataState = (() => {
    if (isTcgCard) return 'not_priceable';
    if (!tiers && !row) return 'no_row';
    if (!tiers && row) return 'row_empty';
    if (tiers && !asks) return 'partial';
    return 'priced';
  })();

  return {
    dataState,

    // The ladder, as rendered.
    hadTiers:     Boolean(tiers),
    tiersFailed:  Boolean(failed),
    tierSource:   tiers?.source || null,
    tierBasis:    tiers?.basis || null,
    ladderRaw:    trimLadder(tiers?.raw),
    ladderGraded: trimLadder(tiers?.graded),
    selectedTier: selectedTier || null,
    shownGrade:   card?.grade || null,

    // The vendor columns beside it. An empty asks map with a report attached is
    // the missing TCGplayer column, stated rather than inferred.
    sourcesShown: sources ? Object.keys(sources) : [],
    asks: asks
      ? Object.fromEntries(Object.entries(asks).map(([k, v]) => [k, money(v?.price)]))
      : null,
    asksPrinting: asks ? (Object.values(asks)[0]?.printing ?? null) : null,
    asksAt: row?.asks_at || null,

    // The cached row everything was drawn from.
    row: row ? {
      id:                 row.id,
      card_name:          row.card_name,
      set_name:           row.set_name,
      set_number:         row.set_number,
      variant:            row.variant,
      is_graded:          row.is_graded,
      search_query:       row.search_query,
      last_refreshed_at:  row.last_refreshed_at,
      last_failure_stage: row.last_failure_stage,
      last_failure_at:    row.last_failure_at,
      failure_count:      row.failure_count,
      retry_after:        row.retry_after,
    } : null,

    // Why the row is empty, when it is. These come straight off the cache row
    // and are the difference between "the vendor has never been asked" and
    // "the vendor was asked eleven times and refused".
    lastFailureStage: row?.last_failure_stage || null,
    failureCount:     row?.failure_count ?? null,
    retryAfter:       row?.retry_after || null,

    // The comps behind the number, newest first. Five is enough to see whether
    // the wrong card got matched; the count says how deep the pool was.
    evidenceCount: evidence.length,
    evidenceSample: newest.slice(0, 5).map((e) => ({
      price:        money(e?.price),
      date:         e?.date || null,
      source:       e?.source || null,
      company:      e?.company || null,
      grade_number: e?.grade_number ?? null,
      condition:    e?.condition || null,
      anomalyFlag:  e?.anomalyFlag || null,
      title:        (e?.title || '').slice(0, 90) || null,
    })),

    reportedAt: new Date().toISOString(),
  };
}

/**
 * Trigger A. The app could not price a card.
 *
 * @param {Object} card        the card as displayed
 * @param {string} stage       one of FAILURE_STAGES
 * @param {Object} diagnostics what the vendor actually returned — query sent,
 *                             candidate count, the candidates themselves
 */
export function reportPricingFailure(card, stage, diagnostics = {}) {
  if (!card?.name || !stage) return;

  const key = sessionKey('auto', card, stage, null);
  if (sent.has(key)) return;
  sent.add(key);

  // Deliberately not awaited.
  write({
    source: 'auto',
    failure_stage: stage,
    diagnostics,
    ...snapshot(card),
  });
}

/**
 * Trigger B. The user says the price is wrong.
 *
 * @param {Object} card
 * @param {string} reasonCode one of REPORT_REASONS[].code
 * @param {string} [note]     free text, optional
 * @param {Object} [context]  what was on screen when they reported it
 * @returns {Promise<'saved'|'duplicate'|'signed_out'|'error'>} what happened.
 *          The sheet tells the user the truth about each of these. Reporting
 *          the same card twice in a day is not a save, and saying "thanks" to
 *          a rejected insert is how a broken pipe stays invisible for a week.
 */
export async function reportPricingComplaint(card, reasonCode, note, context = {}) {
  if (!card?.name || !reasonCode) return 'error';

  const key = sessionKey('user', card, 'user_reported', reasonCode);
  sent.add(key);

  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id || null;

    // The insert policy is `to authenticated`. Without a session the row is
    // refused, and that refusal is worth naming rather than swallowing.
    if (!userId) return 'signed_out';

    const { error } = await supabase.from('pricing_reports').insert({
      source: 'user',
      failure_stage: 'user_reported',
      reason_code: reasonCode,
      note: (note || '').slice(0, 1000) || null,
      diagnostics: context,
      user_id: userId,
      ...snapshot(card),
    });

    if (!error) return 'saved';

    // The dedupe index is one row per card, per user, per day. Hitting it means
    // the report already exists, which is different from having just filed one.
    if (error.code === '23505') return 'duplicate';

    console.warn('[PricingReport] complaint failed:', error.code, error.message);
    return 'error';
  } catch (err) {
    console.warn('[PricingReport] complaint threw:', err?.message);
    return 'error';
  }
}

export default { FAILURE_STAGES, REPORT_REASONS, buildReportContext, reportPricingFailure, reportPricingComplaint };
